import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { Logo } from "@/components/rentalmnp/shared/Logo";

const TITLE = "Mentions légales | RentaLMNP";
const DESC =
  "Mentions légales de RentaLMNP : éditeur, hébergement, propriété intellectuelle, liens d'affiliation.";

export const Route = createFileRoute("/mentions-legales")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://rentalmnp.com/mentions-legales" },
    ],
    links: [{ rel: "canonical", href: "https://rentalmnp.com/mentions-legales" }],
  }),
  component: MentionsPage,
});

function MentionsPage() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-3xl px-6 py-8 lg:px-12">
        <div className="mb-6 flex items-center justify-between">
          <Logo size={36} />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
        </div>

        <h1 className="mt-6 text-3xl font-extrabold text-foreground sm:text-4xl">
          Mentions légales
        </h1>

        <div className="prose prose-sm mt-8 max-w-none space-y-8 text-foreground/90">
          <section>
            <h2 className="text-xl font-bold text-foreground">Éditeur</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              RentaLMNP — simulateur en ligne de rentabilité LMNP. Conçu en
              France.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Hébergement</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Site hébergé sur une infrastructure cloud moderne avec
              chiffrement TLS de bout en bout.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">
              Liens d'affiliation
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              RentaLMNP contient des liens d'affiliation vers des partenaires
              commerciaux (notamment Meilleurtaux.com). Conformément à la
              réglementation en vigueur, nous vous informons que :
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6 text-sm leading-relaxed text-muted-foreground">
              <li>
                Ces liens sont signalés par la mention{" "}
                <strong className="text-foreground">"Lien partenaire"</strong>.
              </li>
              <li>
                Nous percevons une commission si vous souscrivez un service via
                ces liens, <strong className="text-foreground">sans surcoût pour vous</strong>.
              </li>
              <li>Cela nous permet de maintenir ce service gratuit.</li>
              <li>
                Nous sélectionnons nos partenaires sur la base de la qualité du
                service rendu, indépendamment de la commission.
              </li>
            </ul>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Vous pouvez à tout moment accéder directement au site de nos
              partenaires sans passer par nos liens.{" "}
              <Link
                to="/partenaires"
                className="font-medium text-primary hover:underline"
              >
                Voir la liste de nos partenaires
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">
              Propriété intellectuelle
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              L'ensemble des contenus présents sur RentaLMNP (textes,
              graphismes, logos, calculs, code) est protégé par le droit
              d'auteur. Toute reproduction sans autorisation est interdite.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">
              Données personnelles
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Les données saisies dans le simulateur ne sont pas stockées sur
              nos serveurs. Elles restent sur votre appareil (LocalStorage)
              pour vous permettre de reprendre votre simulation. L'email
              éventuellement renseigné sert uniquement à vous envoyer votre
              diagnostic et à reprendre votre simulation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">
              Avertissement
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Les estimations fournies par RentaLMNP sont à titre strictement
              informatif et ne constituent pas un conseil fiscal, juridique ou
              patrimonial. Avant toute décision d'investissement, consultez un
              professionnel qualifié.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
